import type {
  TrafficInvestigationSubscriptionComparison,
  TrafficPerformanceRow,
} from '@/services/api';

export interface SubscriptionCountComparison {
  comparison: number | null;
  current: number | null;
  absoluteChange: number | null;
}

export interface SubscriptionFailureComparison {
  comparison: string;
  current: string;
  change: string;
}

type CountMetric = 'completedCount' | 'failoverCount';
type SubscriptionParty = 'customers' | 'providers';

function compareCount(
  subscription: TrafficInvestigationSubscriptionComparison,
  metric: CountMetric | 'inCount' | 'outCount',
): SubscriptionCountComparison {
  const comparison = subscription.comparison?.[metric] ?? null;
  const current = subscription.current?.[metric] ?? null;
  return {
    comparison,
    current,
    absoluteChange: comparison === null || current === null ? null : current - comparison,
  };
}

export function subscriptionCountComparison(
  subscription: TrafficInvestigationSubscriptionComparison,
  metric: CountMetric,
): SubscriptionCountComparison {
  return compareCount(subscription, metric);
}

export function subscriptionVolumeComparison(
  subscription: TrafficInvestigationSubscriptionComparison,
  party: SubscriptionParty,
): SubscriptionCountComparison {
  return compareCount(subscription, party === 'customers' ? 'inCount' : 'outCount');
}

function failureLabel(row: TrafficPerformanceRow | null): string {
  if (row === null) return 'Unavailable';
  if (row.topFailureCode === null) return 'None';
  return `${row.topFailureCode} · ${row.topFailureCount.toLocaleString()}`;
}

export function subscriptionFailureComparison(
  subscription: TrafficInvestigationSubscriptionComparison,
): SubscriptionFailureComparison {
  const comparison = subscription.comparison;
  const current = subscription.current;
  let change = 'Unavailable';

  if (comparison !== null && current !== null) {
    if (comparison.topFailureCode !== current.topFailureCode) {
      change = 'Code changed';
    } else if (current.topFailureCode === null) {
      change = 'No failure';
    } else {
      const absoluteChange = current.topFailureCount - comparison.topFailureCount;
      change = `${absoluteChange > 0 ? '+' : ''}${absoluteChange.toLocaleString()}`;
    }
  }

  return {
    comparison: failureLabel(comparison),
    current: failureLabel(current),
    change,
  };
}

export function sortSubscriptionComparisons(
  subscriptions: TrafficInvestigationSubscriptionComparison[],
): TrafficInvestigationSubscriptionComparison[] {
  return [...subscriptions].sort((left, right) => {
    const leftChange = subscriptionCountComparison(left, 'completedCount').absoluteChange;
    const rightChange = subscriptionCountComparison(right, 'completedCount').absoluteChange;
    if (leftChange === null && rightChange !== null) return 1;
    if (leftChange !== null && rightChange === null) return -1;
    if (leftChange !== null && rightChange !== null) {
      const changeOrder = Math.abs(rightChange) - Math.abs(leftChange);
      if (changeOrder !== 0) return changeOrder;
    }
    return left.providerRecordName.localeCompare(right.providerRecordName);
  });
}
